
import React, { useState, useEffect, useCallback } from 'react';
import './InvoiceGenerator.css';

// Types
interface LineItem {
  id: string;
  description: string;
  quantity: number;
  rate: number;
  amount: number;
}

interface InvoiceData {
  invoiceNumber: string;
  date: string;
  dueDate: string;
  from: {
    name: string;
    email: string;
    address: string;
    phone: string;
  };
  to: {
    name: string;
    email: string;
    address: string;
    phone: string;
  };
  items: LineItem[];
  notes: string;
  terms: string;
  taxRate: number;
  discountPercent: number;
}

interface EmailConfig {
  smtpHost: string;
  smtpPort: number;
  smtpUser: string;
  smtpPass: string;
  imapHost: string;
  imapPort: number;
  imapUser: string;
  imapPass: string;
  useTLS: boolean;
}

interface PaymentLink {
  url: string;
  qrCode?: string;
  orderId?: string;
}

// Generate unique ID
const generateId = () => crypto.randomUUID?.() || Math.random().toString(36).substr(2, 9);

// Default invoice data
const defaultInvoice: InvoiceData = {
  invoiceNumber: `INV-${Date.now().toString(36).toUpperCase()}`,
  date: new Date().toISOString().split('T')[0],
  dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  from: {
    name: 'Your Company Name',
    email: 'billing@yourcompany.com',
    address: '123 Business St, Suite 100\nCity, State 12345',
    phone: '(555) 123-4567',
  },
  to: {
    name: '',
    email: '',
    address: '',
    phone: '',
  },
  items: [
    { id: generateId(), description: '', quantity: 1, rate: 0, amount: 0 },
  ],
  notes: 'Thank you for your business!',
  terms: 'Payment is due within 30 days. Late payments may incur a 1.5% monthly fee.',
  taxRate: 0,
  discountPercent: 0,
};

// Default email config
const defaultEmailConfig: EmailConfig = {
  smtpHost: 'smtp.gmail.com',
  smtpPort: 587,
  smtpUser: '',
  smtpPass: '',
  imapHost: 'imap.gmail.com',
  imapPort: 993,
  imapUser: '',
  imapPass: '',
  useTLS: true,
};

export default function InvoiceGenerator() {
  const [invoice, setInvoice] = useState<InvoiceData>(defaultInvoice);
  const [emailConfig, setEmailConfig] = useState<EmailConfig>(defaultEmailConfig);
  const [paymentLink, setPaymentLink] = useState<PaymentLink | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showEmailConfig, setShowEmailConfig] = useState(false);
  const [sending, setSending] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Load saved config from localStorage
  useEffect(() => {
    const savedConfig = localStorage.getItem('invoice_email_config');
    if (savedConfig) {
      try {
        setEmailConfig(JSON.parse(savedConfig));
      } catch (e) {
        console.error('Failed to load email config:', e);
      }
    }

    const savedFrom = localStorage.getItem('invoice_from_details');
    if (savedFrom) {
      try {
        setInvoice(prev => ({ ...prev, from: JSON.parse(savedFrom) }));
      } catch (e) {
        console.error('Failed to load from details:', e);
      }
    }
  }, []);

  // Calculate totals
  const subtotal = invoice.items.reduce((sum, item) => sum + item.amount, 0);
  const discount = subtotal * (invoice.discountPercent / 100);
  const taxableAmount = subtotal - discount;
  const tax = taxableAmount * (invoice.taxRate / 100);
  const total = taxableAmount + tax;

  // Update line item
  const updateItem = (id: string, field: keyof LineItem, value: string | number) => {
    setInvoice(prev => ({
      ...prev,
      items: prev.items.map(item => {
        if (item.id !== id) return item;
        const updated = { ...item, [field]: value };
        if (field === 'quantity' || field === 'rate') {
          updated.amount = updated.quantity * updated.rate;
        }
        return updated;
      }),
    }));
  };

  // Add line item
  const addItem = () => {
    setInvoice(prev => ({
      ...prev,
      items: [...prev.items, { id: generateId(), description: '', quantity: 1, rate: 0, amount: 0 }],
    }));
  };

  // Remove line item
  const removeItem = (id: string) => {
    if (invoice.items.length <= 1) return;
    setInvoice(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== id),
    }));
  };

  // Generate PayPal payment link
  const generatePaymentLink = async () => {
    if (total <= 0) {
      setMessage({ type: 'error', text: 'Invoice total must be greater than $0' });
      return;
    }

    setGenerating(true);
    setMessage(null);

    try {
      // Call PayPal API to create order and generate payment link
      const response = await fetch('/api/paypal/create-invoice-payment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`,
        },
        body: JSON.stringify({
          invoiceNumber: invoice.invoiceNumber,
          amount: total.toFixed(2),
          currency: 'USD',
          description: `Invoice ${invoice.invoiceNumber} - ${invoice.to.name || 'Customer'}`,
          items: invoice.items.map(item => ({
            name: item.description || 'Service',
            quantity: item.quantity,
            unit_amount: item.rate.toFixed(2),
          })),
          customerEmail: invoice.to.email,
          returnUrl: `${window.location.origin}/invoices/success`,
          cancelUrl: `${window.location.origin}/invoices/cancel`,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate payment link');
      }

      const data = await response.json();
      setPaymentLink({
        url: data.paymentUrl,
        qrCode: data.qrCode,
        orderId: data.orderId,
      });
      setMessage({ type: 'success', text: 'Payment link generated successfully!' });
    } catch (error) {
      console.error('Error generating payment link:', error);
      // Fallback to direct PayPal.me link
      const paypalUsername = localStorage.getItem('paypal_username') || 'yourpaypal';
      const fallbackUrl = `https://paypal.me/${paypalUsername}/${total.toFixed(2)}USD`;
      setPaymentLink({ url: fallbackUrl });
      setMessage({ type: 'success', text: 'Payment link ready (using PayPal.me fallback)' });
    } finally {
      setGenerating(false);
    }
  };

  // Send invoice via email
  const sendInvoice = async () => {
    if (!invoice.to.email) {
      setMessage({ type: 'error', text: 'Please enter recipient email address' });
      return;
    }

    if (!emailConfig.smtpUser || !emailConfig.smtpPass) {
      setMessage({ type: 'error', text: 'Please configure email settings first' });
      setShowEmailConfig(true);
      return;
    }

    setSending(true);
    setMessage(null);

    try {
      const response = await fetch('/api/email/send-invoice', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`,
        },
        body: JSON.stringify({
          smtp: {
            host: emailConfig.smtpHost,
            port: emailConfig.smtpPort,
            user: emailConfig.smtpUser,
            pass: emailConfig.smtpPass,
            secure: emailConfig.useTLS,
          },
          to: invoice.to.email,
          subject: `Invoice ${invoice.invoiceNumber} from ${invoice.from.name}`,
          invoice: {
            ...invoice,
            subtotal,
            discount,
            tax,
            total,
          },
          paymentLink: paymentLink?.url,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send email');
      }

      setMessage({ type: 'success', text: `Invoice sent to ${invoice.to.email}` });
    } catch (error) {
      console.error('Error sending invoice:', error);
      setMessage({ type: 'error', text: 'Failed to send invoice. Check email settings.' });
    } finally {
      setSending(false);
    }
  };

  // Save email config
  const saveEmailConfig = () => {
    localStorage.setItem('invoice_email_config', JSON.stringify(emailConfig));
    localStorage.setItem('invoice_from_details', JSON.stringify(invoice.from));
    setShowEmailConfig(false);
    setMessage({ type: 'success', text: 'Settings saved!' });
  };

  // Copy payment link
  const copyPaymentLink = () => {
    if (paymentLink?.url) {
      navigator.clipboard.writeText(paymentLink.url);
      setMessage({ type: 'success', text: 'Payment link copied to clipboard!' });
    }
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <div className="invoice-generator">
      {/* Header */}
      <div className="invoice-header">
        <div className="invoice-header__left">
          <h1 className="invoice-header__title">Invoice Generator</h1>
          <p className="invoice-header__subtitle">Create, preview, and send professional invoices with PayPal payment links</p>
        </div>
        <div className="invoice-header__actions">
          <button className="btn btn--ghost" onClick={() => setShowEmailConfig(true)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="3"/><path d="M12 1v6m0 6v6m11-7h-6m-6 0H1m15.36-5.36l-4.24 4.24m0 0L6.88 6.64m5.24 5.24l4.24 4.24m-9.48 0l4.24-4.24"/>
            </svg>
            Settings
          </button>
          <button className="btn btn--secondary" onClick={() => setShowPreview(true)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
            </svg>
            Preview
          </button>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`invoice-message invoice-message--${message.type}`}>
          {message.text}
          <button onClick={() => setMessage(null)}>×</button>
        </div>
      )}

      <div className="invoice-content">
        {/* Invoice Form */}
        <div className="invoice-form">
          {/* Invoice Details */}
          <div className="invoice-section">
            <h3 className="invoice-section__title">Invoice Details</h3>
            <div className="invoice-grid invoice-grid--3">
              <div className="invoice-field">
                <label>Invoice Number</label>
                <input
                  type="text"
                  value={invoice.invoiceNumber}
                  onChange={e => setInvoice(prev => ({ ...prev, invoiceNumber: e.target.value }))}
                />
              </div>
              <div className="invoice-field">
                <label>Invoice Date</label>
                <input
                  type="date"
                  value={invoice.date}
                  onChange={e => setInvoice(prev => ({ ...prev, date: e.target.value }))}
                />
              </div>
              <div className="invoice-field">
                <label>Due Date</label>
                <input
                  type="date"
                  value={invoice.dueDate}
                  onChange={e => setInvoice(prev => ({ ...prev, dueDate: e.target.value }))}
                />
              </div>
            </div>
          </div>

          {/* From/To */}
          <div className="invoice-parties">
            <div className="invoice-section">
              <h3 className="invoice-section__title">From</h3>
              <div className="invoice-field">
                <label>Business Name</label>
                <input
                  type="text"
                  value={invoice.from.name}
                  onChange={e => setInvoice(prev => ({ ...prev, from: { ...prev.from, name: e.target.value } }))}
                />
              </div>
              <div className="invoice-field">
                <label>Email</label>
                <input
                  type="email"
                  value={invoice.from.email}
                  onChange={e => setInvoice(prev => ({ ...prev, from: { ...prev.from, email: e.target.value } }))}
                />
              </div>
              <div className="invoice-field">
                <label>Address</label>
                <textarea
                  value={invoice.from.address}
                  onChange={e => setInvoice(prev => ({ ...prev, from: { ...prev.from, address: e.target.value } }))}
                  rows={2}
                />
              </div>
              <div className="invoice-field">
                <label>Phone</label>
                <input
                  type="tel"
                  value={invoice.from.phone}
                  onChange={e => setInvoice(prev => ({ ...prev, from: { ...prev.from, phone: e.target.value } }))}
                />
              </div>
            </div>

            <div className="invoice-section">
              <h3 className="invoice-section__title">Bill To</h3>
              <div className="invoice-field">
                <label>Client Name</label>
                <input
                  type="text"
                  value={invoice.to.name}
                  onChange={e => setInvoice(prev => ({ ...prev, to: { ...prev.to, name: e.target.value } }))}
                  placeholder="Client or Company Name"
                />
              </div>
              <div className="invoice-field">
                <label>Email</label>
                <input
                  type="email"
                  value={invoice.to.email}
                  onChange={e => setInvoice(prev => ({ ...prev, to: { ...prev.to, email: e.target.value } }))}
                  placeholder="client@email.com"
                />
              </div>
              <div className="invoice-field">
                <label>Address</label>
                <textarea
                  value={invoice.to.address}
                  onChange={e => setInvoice(prev => ({ ...prev, to: { ...prev.to, address: e.target.value } }))}
                  rows={2}
                  placeholder="Client address"
                />
              </div>
              <div className="invoice-field">
                <label>Phone</label>
                <input
                  type="tel"
                  value={invoice.to.phone}
                  onChange={e => setInvoice(prev => ({ ...prev, to: { ...prev.to, phone: e.target.value } }))}
                  placeholder="(555) 000-0000"
                />
              </div>
            </div>
          </div>

          {/* Line Items */}
          <div className="invoice-section">
            <h3 className="invoice-section__title">Line Items</h3>
            <div className="invoice-items">
              <div className="invoice-items__header">
                <span className="col-desc">Description</span>
                <span className="col-qty">Qty</span>
                <span className="col-rate">Rate</span>
                <span className="col-amount">Amount</span>
                <span className="col-action"></span>
              </div>
              {invoice.items.map((item, index) => (
                <div key={item.id} className="invoice-items__row">
                  <input
                    className="col-desc"
                    type="text"
                    value={item.description}
                    onChange={e => updateItem(item.id, 'description', e.target.value)}
                    placeholder="Service or product description"
                  />
                  <input
                    className="col-qty"
                    type="number"
                    min="1"
                    value={item.quantity}
                    onChange={e => updateItem(item.id, 'quantity', parseInt(e.target.value) || 1)}
                  />
                  <div className="col-rate input-with-prefix">
                    <span>$</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.rate}
                      onChange={e => updateItem(item.id, 'rate', parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <span className="col-amount">{formatCurrency(item.amount)}</span>
                  <button
                    className="col-action btn-icon btn-icon--danger"
                    onClick={() => removeItem(item.id)}
                    disabled={invoice.items.length <= 1}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
                    </svg>
                  </button>
                </div>
              ))}
              <button className="btn btn--ghost btn--sm" onClick={addItem}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Add Line Item
              </button>
            </div>
          </div>

          {/* Totals */}
          <div className="invoice-section">
            <div className="invoice-totals">
              <div className="invoice-totals__row">
                <span>Subtotal</span>
                <span>{formatCurrency(subtotal)}</span>
              </div>
              <div className="invoice-totals__row invoice-totals__row--editable">
                <div className="invoice-field invoice-field--inline">
                  <label>Discount</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={invoice.discountPercent}
                    onChange={e => setInvoice(prev => ({ ...prev, discountPercent: parseFloat(e.target.value) || 0 }))}
                  />
                  <span>%</span>
                </div>
                <span className="text-danger">-{formatCurrency(discount)}</span>
              </div>
              <div className="invoice-totals__row invoice-totals__row--editable">
                <div className="invoice-field invoice-field--inline">
                  <label>Tax</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={invoice.taxRate}
                    onChange={e => setInvoice(prev => ({ ...prev, taxRate: parseFloat(e.target.value) || 0 }))}
                  />
                  <span>%</span>
                </div>
                <span>{formatCurrency(tax)}</span>
              </div>
              <div className="invoice-totals__row invoice-totals__row--total">
                <span>Total Due</span>
                <span>{formatCurrency(total)}</span>
              </div>
            </div>
          </div>

          {/* Notes & Terms */}
          <div className="invoice-grid invoice-grid--2">
            <div className="invoice-section">
              <h3 className="invoice-section__title">Notes</h3>
              <textarea
                value={invoice.notes}
                onChange={e => setInvoice(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
                placeholder="Additional notes for the client..."
              />
            </div>
            <div className="invoice-section">
              <h3 className="invoice-section__title">Terms & Conditions</h3>
              <textarea
                value={invoice.terms}
                onChange={e => setInvoice(prev => ({ ...prev, terms: e.target.value }))}
                rows={3}
                placeholder="Payment terms..."
              />
            </div>
          </div>
        </div>

        {/* Payment & Actions Panel */}
        <div className="invoice-sidebar">
          <div className="invoice-card">
            <h3 className="invoice-card__title">Payment Link</h3>
            <p className="invoice-card__desc">Generate a PayPal payment link for this invoice</p>

            {paymentLink ? (
              <div className="payment-link-result">
                <div className="payment-link-url">
                  <input type="text" value={paymentLink.url} readOnly />
                  <button className="btn btn--sm btn--primary" onClick={copyPaymentLink}>
                    Copy
                  </button>
                </div>
                {paymentLink.qrCode && (
                  <div className="payment-link-qr">
                    <img src={paymentLink.qrCode} alt="Payment QR Code" />
                  </div>
                )}
                <button className="btn btn--ghost btn--sm" onClick={() => setPaymentLink(null)}>
                  Generate New Link
                </button>
              </div>
            ) : (
              <button
                className="btn btn--primary btn--full"
                onClick={generatePaymentLink}
                disabled={generating || total <= 0}
              >
                {generating ? (
                  <>
                    <span className="spinner"></span>
                    Generating...
                  </>
                ) : (
                  <>
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797H9.31c-.52 0-.964.382-1.045.9l-.001.005-.002.011-.001.005-1.184 7.085z"/>
                    </svg>
                    Generate PayPal Link
                  </>
                )}
              </button>
            )}

            <div className="invoice-card__total">
              <span>Invoice Total</span>
              <span className="total-amount">{formatCurrency(total)}</span>
            </div>
          </div>

          <div className="invoice-card">
            <h3 className="invoice-card__title">Send Invoice</h3>
            <p className="invoice-card__desc">Email the invoice directly to your client</p>

            <div className="invoice-field">
              <label>Send To</label>
              <input
                type="email"
                value={invoice.to.email}
                onChange={e => setInvoice(prev => ({ ...prev, to: { ...prev.to, email: e.target.value } }))}
                placeholder="client@email.com"
              />
            </div>

            <button
              className="btn btn--secondary btn--full"
              onClick={sendInvoice}
              disabled={sending || !invoice.to.email}
            >
              {sending ? (
                <>
                  <span className="spinner"></span>
                  Sending...
                </>
              ) : (
                <>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                  Send Invoice
                </>
              )}
            </button>
          </div>

          <div className="invoice-card">
            <h3 className="invoice-card__title">Quick Actions</h3>
            <div className="quick-actions">
              <button className="btn btn--ghost btn--sm btn--full" onClick={() => setShowPreview(true)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
                </svg>
                Preview PDF
              </button>
              <button className="btn btn--ghost btn--sm btn--full" onClick={() => window.print()}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                  <rect x="6" y="14" width="12" height="8"/>
                </svg>
                Print Invoice
              </button>
              <button className="btn btn--ghost btn--sm btn--full">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Download PDF
              </button>
              <button
                className="btn btn--ghost btn--sm btn--full"
                onClick={() => setInvoice({
                  ...defaultInvoice,
                  invoiceNumber: `INV-${Date.now().toString(36).toUpperCase()}`,
                  from: invoice.from,
                })}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
                </svg>
                New Invoice
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="invoice-modal-overlay" onClick={() => setShowPreview(false)}>
          <div className="invoice-modal invoice-modal--preview" onClick={e => e.stopPropagation()}>
            <div className="invoice-modal__header">
              <h2>Invoice Preview</h2>
              <button className="btn-close" onClick={() => setShowPreview(false)}>×</button>
            </div>
            <div className="invoice-preview">
              <div className="invoice-preview__content">
                {/* Invoice Header */}
                <div className="preview-header">
                  <div className="preview-logo">
                    <h1>{invoice.from.name}</h1>
                    <p>{invoice.from.email}</p>
                  </div>
                  <div className="preview-title">
                    <h2>INVOICE</h2>
                    <p><strong>{invoice.invoiceNumber}</strong></p>
                  </div>
                </div>

                {/* Parties */}
                <div className="preview-parties">
                  <div className="preview-from">
                    <h4>From</h4>
                    <p><strong>{invoice.from.name}</strong></p>
                    <p style={{ whiteSpace: 'pre-line' }}>{invoice.from.address}</p>
                    <p>{invoice.from.phone}</p>
                    <p>{invoice.from.email}</p>
                  </div>
                  <div className="preview-to">
                    <h4>Bill To</h4>
                    <p><strong>{invoice.to.name || 'Client Name'}</strong></p>
                    <p style={{ whiteSpace: 'pre-line' }}>{invoice.to.address || 'Client Address'}</p>
                    <p>{invoice.to.phone}</p>
                    <p>{invoice.to.email}</p>
                  </div>
                  <div className="preview-dates">
                    <p><strong>Invoice Date:</strong> {invoice.date}</p>
                    <p><strong>Due Date:</strong> {invoice.dueDate}</p>
                  </div>
                </div>

                {/* Items Table */}
                <table className="preview-table">
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>Qty</th>
                      <th>Rate</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.items.map(item => (
                      <tr key={item.id}>
                        <td>{item.description || 'Service'}</td>
                        <td>{item.quantity}</td>
                        <td>{formatCurrency(item.rate)}</td>
                        <td>{formatCurrency(item.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Totals */}
                <div className="preview-totals">
                  <div className="preview-totals__row">
                    <span>Subtotal</span>
                    <span>{formatCurrency(subtotal)}</span>
                  </div>
                  {invoice.discountPercent > 0 && (
                    <div className="preview-totals__row">
                      <span>Discount ({invoice.discountPercent}%)</span>
                      <span>-{formatCurrency(discount)}</span>
                    </div>
                  )}
                  {invoice.taxRate > 0 && (
                    <div className="preview-totals__row">
                      <span>Tax ({invoice.taxRate}%)</span>
                      <span>{formatCurrency(tax)}</span>
                    </div>
                  )}
                  <div className="preview-totals__row preview-totals__row--total">
                    <span>Total Due</span>
                    <span>{formatCurrency(total)}</span>
                  </div>
                </div>

                {/* Payment Link */}
                {paymentLink && (
                  <div className="preview-payment">
                    <h4>Pay Online</h4>
                    <a href={paymentLink.url} target="_blank" rel="noopener noreferrer" className="btn btn--primary">
                      Pay {formatCurrency(total)} with PayPal
                    </a>
                  </div>
                )}

                {/* Notes & Terms */}
                {invoice.notes && (
                  <div className="preview-notes">
                    <h4>Notes</h4>
                    <p>{invoice.notes}</p>
                  </div>
                )}
                {invoice.terms && (
                  <div className="preview-terms">
                    <h4>Terms & Conditions</h4>
                    <p>{invoice.terms}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Config Modal */}
      {showEmailConfig && (
        <div className="invoice-modal-overlay" onClick={() => setShowEmailConfig(false)}>
          <div className="invoice-modal" onClick={e => e.stopPropagation()}>
            <div className="invoice-modal__header">
              <h2>Email Settings</h2>
              <button className="btn-close" onClick={() => setShowEmailConfig(false)}>×</button>
            </div>
            <div className="invoice-modal__content">
              <div className="config-section">
                <h3>SMTP Settings (Outgoing Mail)</h3>
                <div className="invoice-grid invoice-grid--2">
                  <div className="invoice-field">
                    <label>SMTP Host</label>
                    <input
                      type="text"
                      value={emailConfig.smtpHost}
                      onChange={e => setEmailConfig(prev => ({ ...prev, smtpHost: e.target.value }))}
                      placeholder="smtp.gmail.com"
                    />
                  </div>
                  <div className="invoice-field">
                    <label>Port</label>
                    <input
                      type="number"
                      value={emailConfig.smtpPort}
                      onChange={e => setEmailConfig(prev => ({ ...prev, smtpPort: parseInt(e.target.value) || 587 }))}
                    />
                  </div>
                  <div className="invoice-field">
                    <label>Username / Email</label>
                    <input
                      type="email"
                      value={emailConfig.smtpUser}
                      onChange={e => setEmailConfig(prev => ({ ...prev, smtpUser: e.target.value }))}
                      placeholder="your@email.com"
                    />
                  </div>
                  <div className="invoice-field">
                    <label>Password / App Password</label>
                    <input
                      type="password"
                      value={emailConfig.smtpPass}
                      onChange={e => setEmailConfig(prev => ({ ...prev, smtpPass: e.target.value }))}
                      placeholder="••••••••"
                    />
                  </div>
                </div>
              </div>

              <div className="config-section">
                <h3>IMAP Settings (Incoming Mail)</h3>
                <div className="invoice-grid invoice-grid--2">
                  <div className="invoice-field">
                    <label>IMAP Host</label>
                    <input
                      type="text"
                      value={emailConfig.imapHost}
                      onChange={e => setEmailConfig(prev => ({ ...prev, imapHost: e.target.value }))}
                      placeholder="imap.gmail.com"
                    />
                  </div>
                  <div className="invoice-field">
                    <label>Port</label>
                    <input
                      type="number"
                      value={emailConfig.imapPort}
                      onChange={e => setEmailConfig(prev => ({ ...prev, imapPort: parseInt(e.target.value) || 993 }))}
                    />
                  </div>
                  <div className="invoice-field">
                    <label>Username / Email</label>
                    <input
                      type="email"
                      value={emailConfig.imapUser}
                      onChange={e => setEmailConfig(prev => ({ ...prev, imapUser: e.target.value }))}
                      placeholder="your@email.com"
                    />
                  </div>
                  <div className="invoice-field">
                    <label>Password / App Password</label>
                    <input
                      type="password"
                      value={emailConfig.imapPass}
                      onChange={e => setEmailConfig(prev => ({ ...prev, imapPass: e.target.value }))}
                      placeholder="••••••••"
                    />
                  </div>
                </div>
              </div>

              <div className="config-section">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={emailConfig.useTLS}
                    onChange={e => setEmailConfig(prev => ({ ...prev, useTLS: e.target.checked }))}
                  />
                  <span>Use TLS/SSL encryption</span>
                </label>
              </div>

              <div className="config-section">
                <h3>PayPal Settings</h3>
                <div className="invoice-field">
                  <label>PayPal.me Username</label>
                  <input
                    type="text"
                    defaultValue={localStorage.getItem('paypal_username') || ''}
                    onChange={e => localStorage.setItem('paypal_username', e.target.value)}
                    placeholder="yourpaypal"
                  />
                  <small>Used for fallback payment links (paypal.me/yourpaypal)</small>
                </div>
              </div>
            </div>
            <div className="invoice-modal__footer">
              <button className="btn btn--ghost" onClick={() => setShowEmailConfig(false)}>Cancel</button>
              <button className="btn btn--primary" onClick={saveEmailConfig}>Save Settings</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
